import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  attachLots,
  clearCCC,
  deleteCCCDoc,
  detachLot,
  issueCCC,
  registerCCCDoc,
  reprocessLot,
} from "@/features/transit/api/transit.api"
import type {
  AttachLotsPayload,
  IssueCCCPayload,
  RegisterCCCDocPayload,
} from "@/features/transit/api/types"
import {
  transitKeys,
  useTransitOrders,
} from "@/features/transit/hooks/use-transit-orders"
import { resolveApiError } from "@/lib/api/error-i18n"

// Re-export so callers can stay in one import.
export { useTransitOrders }

function useInvalidate(otId: number | string) {
  const queryClient = useQueryClient()
  return async () => {
    // Refetch the active detail synchronously — see comment in
    // `use-transit-mutations.ts` for why we don't trust the response body.
    await queryClient.refetchQueries({
      queryKey: transitKeys.detail(otId),
      type: "active",
    })
    await queryClient.invalidateQueries({ queryKey: transitKeys.all })
    await queryClient.invalidateQueries({
      queryKey: ["transit", "lots-picker"],
    })
  }
}

function useToastError() {
  const { t } = useTranslation("transit")
  return (error: unknown) => toast.error(resolveApiError(error, t))
}

export function useAttachLots(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: AttachLotsPayload) => attachLots(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDetachLot(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (lotId: number | string) => detachLot(otId, lotId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useIssueCCC(otId: number | string) {
  const { t } = useTranslation("transit")
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      lotId,
      payload,
    }: {
      lotId: number | string
      payload: IssueCCCPayload
    }) => issueCCC(otId, lotId, payload),
    onSuccess: async (_data, vars) => {
      toast.success(
        vars.payload.kind === "BV"
          ? t("toast.cccBVIssued")
          : t("toast.cccBAIssued"),
      )
      await invalidate()
    },
    onError: toastError,
  })
}

export function useClearCCC(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (lotId: number | string) => clearCCC(otId, lotId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useRegisterCCCDoc(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      lotId,
      payload,
    }: {
      lotId: number | string
      payload: RegisterCCCDocPayload
    }) => registerCCCDoc(otId, lotId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeleteCCCDoc(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      lotId,
      docId,
    }: {
      lotId: number | string
      docId: string
    }) => deleteCCCDoc(otId, lotId, docId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useReprocessLot(otId: number | string) {
  const { t } = useTranslation("transit")
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (lotId: number | string) => reprocessLot(otId, lotId),
    onSuccess: async () => {
      toast.success(t("toast.lotReprocessing"))
      await invalidate()
    },
    onError: toastError,
  })
}
